import equals from 'fast-deep-equal';

import { portalApi } from '../../../api';
import presetFilters, { presetFilterNames } from '../../../pages/ReportsPage/components/ReportTable/presetFilters';
import { removeArrayItemAtIndex, subtractArrays } from '../../../utils/arrayUtil';
import { createErrorToast, createSuccessToast, createToast } from '../app';
import { selectDepartmentJobs } from '../department';
import { insuredSearch } from '../insureds';

/* STATE
========================================================= */
const DEFAULT_STATE = {
  isLoading: false,
  isQuickFilterDataFetched: false,
  selectedPresets: [
    presetFilterNames.records
  ],
  checkAllChecked: false,
  checked: {},
  stats: {
    expiredCount: 0,
    pendingCount: 0,
    nonCompliantCount: 0,
    delinquentCount: 0,
    insuredCount: 0
  },
  presetJobs: null
};

/* Actions
================================================== */
const REPORTS_GET_STATS_SUCCESS = 'REPORTS_GET_STATS_SUCCESS';
const REPORTS_GET_STATS_FAILURE = 'REPORTS_GET_STATS_FAILURE';
export const getReportsStats = (companyIds = []) => async (dispatch, getState) => {
  const boolFilter = insuredSearch.selectBoolFilter(getState());

  try {
    const stats = await portalApi.getInsuranceRequirementStats(boolFilter, companyIds);
    dispatch({ type: REPORTS_GET_STATS_SUCCESS, stats });
  } catch (error) {
    dispatch({ type: REPORTS_GET_STATS_FAILURE, error });
  }
};

const REPORTS_TOGGLE_CHECK_ALL = 'REPORTS_TOGGLE_CHECK_ALL';
export const reportsToggleCheckAll = (isChecked = true, data) => dispatch => {
  dispatch({ type: REPORTS_TOGGLE_CHECK_ALL, isChecked, data });
};

const REPORTS_TOGGLE_ROW_CHECK = 'REPORTS_TOGGLE_ROW_CHECK';
export const reportsToggleChecked = (record, value) => dispatch => {
  const { insuredId, jobId } = record;

  dispatch({ type: REPORTS_TOGGLE_ROW_CHECK, record, value });
};

const REPORTS_RESET_CHECKED = 'REPORTS_RESET_CHECKED';
export const reportsResetChecked = () => dispatch => {
  dispatch({ type: REPORTS_RESET_CHECKED });
};

const REPORTS_SELECT_PRESET = 'REPORTS_SELECT_PRESET';
export const reportsSelectPreset = name => (dispatch, getState) => {
  const advancedFilters = insuredSearch.selectAdvancedFilters(getState());
  const columns = insuredSearch.selectColumns(getState());
  const currentHiddenColumnIds = insuredSearch.selectHiddenColumnIds(getState());
  const presetFilter = presetFilters[name];

  if (name === presetFilterNames.records) {
    dispatch(insuredSearch.resetFilters());
  } else {
    const visibleColumnIds = columns
      .filter(c => presetFilter.columns.includes(c.name))
      .map(c => c.id || c.accessor);

    dispatch(insuredSearch.setAdvancedFilters([
      ...advancedFilters,
      ...presetFilter.advancedFilters
    ]));

    dispatch(insuredSearch
      .setHiddenColumnIds(subtractArrays(
        currentHiddenColumnIds,
        visibleColumnIds
      )));
  }

  dispatch({ type: REPORTS_SELECT_PRESET, name });
};

const REPORTS_SEND_EXPORT_EXCEL_EMAIL = 'REPORTS_SEND_EXPORT_EXCEL_EMAIL';
const REPORTS_SEND_EXPORT_EXCEL_EMAIL_SUCCESS = 'REPORTS_SEND_EXPORT_EXCEL_EMAIL_SUCCESS';
const REPORTS_SEND_EXPORT_EXCEL_EMAIL_FAILURE = 'REPORTS_SEND_EXPORT_EXCEL_EMAIL_FAILURE';
export const sendExportReportExcelEmail = (
  email,
  cc,
  searchParams,
  withItems = true,
  includePoliciesAndRequirements = false,
  groupByCodes = false
) => async (dispatch, getState) => {
  const columns = insuredSearch.selectColumns(getState());
  const currentHiddenColumnIds = insuredSearch.selectHiddenColumnIds(getState());
  const visibleColumns = columns.filter(c => !currentHiddenColumnIds.includes(c.id || c.accessor));
  const { companyId } = getState().company;

  dispatch({ type: REPORTS_SEND_EXPORT_EXCEL_EMAIL, email, cc, searchParams, visibleColumns });

  dispatch(createToast({ title: 'Processing Export Request...' }));
  try {
    const response = await portalApi.sendExportExcelEmail(email, cc, searchParams, columns, companyId, withItems, includePoliciesAndRequirements, groupByCodes);
    dispatch({ type: REPORTS_SEND_EXPORT_EXCEL_EMAIL_SUCCESS, response, email, cc, searchParams, visibleColumns });

    dispatch(createSuccessToast({ title: 'Export Request Initiated!' }));
  } catch (error) {
    dispatch({ type: REPORTS_SEND_EXPORT_EXCEL_EMAIL_FAILURE, error });

    dispatch(createErrorToast({
      title: 'Oops... something went wrong with your Export Request',
      error
    }));
  }

  return { success: true, message: 'The recipients should receive the excel report in a few minutes.' };
};

const REPORTS_SEND_DOWNLOAD_DOCUMENTS = 'REPORTS_SEND_DOWNLOAD_DOCUMENTS';
const REPORTS_SEND_DOWNLOAD_DOCUMENTS_SUCCESS = 'REPORTS_SEND_DOWNLOAD_DOCUMENTS_SUCCESS';
const REPORTS_SEND_DOWNLOAD_DOCUMENTS_FAILURE = 'REPORTS_SEND_DOWNLOAD_DOCUMENTS_FAILURE';

export const sendDownloadDocumentsEmail = (email, cc, searchParams) => async (dispatch, getState) => {
  const { companyId } = getState().company;

  dispatch({ type: REPORTS_SEND_DOWNLOAD_DOCUMENTS, email, cc, searchParams });

  dispatch(createToast({ title: 'Processing Download Request...' }));

  try {
    const response = await portalApi.sendDownloadDocumentsEmail(email, cc, searchParams, companyId);
    dispatch({ type: REPORTS_SEND_DOWNLOAD_DOCUMENTS_SUCCESS, response, email, cc, searchParams });

    dispatch(createSuccessToast({ title: 'Download Request Initiated!' }));
  } catch (error) {
    dispatch({ type: REPORTS_SEND_DOWNLOAD_DOCUMENTS_FAILURE, error });

    dispatch(createErrorToast({
      title: 'Oops... something went wrong with your Download Request',
      error
    }));
  }

  return { success: true, message: 'The recipients should receive the excel report in a few minutes.' };
};

const REPORTS_DESELECT_PRESET = 'REPORTS_DESELECT_PRESET';
export const reportsDeselectPreset = name => (dispatch, getState) => {
  const advancedFilters = insuredSearch.selectAdvancedFilters(getState());
  const presetFilter = presetFilters[name];
  const updatedAdvnacedFilters = advancedFilters
    .filter(advancedFilter => !presetFilter
      .advancedFilters
      .find(preset => equals(preset, advancedFilter)));

  dispatch({ type: REPORTS_DESELECT_PRESET, name });
  dispatch(insuredSearch.setAdvancedFilters(updatedAdvnacedFilters));
};

const REPORTS_RESET_PRESETS = 'REPORTS_RESET_PRESETS';
export const reportsResetPresets = () => dispatch => {
  dispatch({ type: REPORTS_RESET_PRESETS });
};

const REPORTS_RESET_ALL = 'REPORTS_RESET_ALL';
export const reportsResetAll = () => dispatch => {
  dispatch({ type: REPORTS_RESET_ALL });
};

const reportsReducer = (state = DEFAULT_STATE, action) => {
  switch (action.type) {
    case REPORTS_SELECT_PRESET: {
      if (action.name === presetFilterNames.records) {
        return {
          ...state,
          selectedPresets: [action.name]
        };
      }

      const currentInsuredsIndex = state.selectedPresets.indexOf(presetFilterNames.records);

      if (currentInsuredsIndex >= 0) {
        return {
          ...state,
          selectedPresets: [
            ...removeArrayItemAtIndex(
              state.selectedPresets,
              currentInsuredsIndex
            ),
            action.name
          ]
        };
      }

      return {
        ...state,
        selectedPresets: [...state.selectedPresets, action.name]
      };
    }

    case REPORTS_DESELECT_PRESET: {
      const presetIndex = state.selectedPresets.findIndex(preset => preset === action.name);

      return {
        ...state,
        selectedPresets: removeArrayItemAtIndex(state.selectedPresets, presetIndex)
      };
    }

    case REPORTS_TOGGLE_CHECK_ALL: {
      if (!action.isChecked) {
        return {
          ...state,
          checked: DEFAULT_STATE.checked,
          checkAllChecked: DEFAULT_STATE.checkAllChecked
        };
      }

      const checked = action.data.reduce((all, current) => ({
        ...all,
        [current.insured.id]: {
          jobId: current.job.id,
          insuredId: current.insured.id
        }
      }), {});

      return {
        ...state,
        checked,
        checkAllChecked: action.isChecked
      };
    }

    case REPORTS_TOGGLE_ROW_CHECK: {
      const { record } = action;
      const { insuredId, jobId, insuredName, insuredIntegrations, insuredExternalId } = record;

      if (state.checked[insuredId]) {
        const { [insuredId]: itemToRemove, ...checked } = state.checked;
        return { ...state, checked, checkAllChecked: false };
      }

      return {
        ...state,
        checked: {
          ...state.checked,
          [insuredId]: { insuredId, jobId, insuredName, insuredIntegrations, insuredExternalId }
        }
      };
    }

    case REPORTS_RESET_CHECKED: {
      return {
        ...state,
        checked: {},
        checkAllChecked: false
      };
    }

    case REPORTS_GET_STATS_SUCCESS: {
      return {
        ...state,
        stats: action.stats
      };
    }

    case REPORTS_RESET_PRESETS: {
      return {
        ...state,
        selectedPresets: DEFAULT_STATE.selectedPresets
      };
    }

    case REPORTS_RESET_ALL:
      return DEFAULT_STATE;

    default:
      return state;
  }
};

/* Selectors
========================================================= */
export const selectReports = state => state.reports;
export const selectReportsChecked = state => state.reports.checked;
export const selectReportsIsRowChecked = insuredId => state => state.reports.checked[insuredId];
export const selectReportsCheckAllChecked = state => state.reports.checkAllChecked;
export const selectReportsPresets = state => state.reports.selectedPresets;
export const selectReportsStats = state => state.reports.stats;

export default reportsReducer;
